import {
  hasCompletedOnboarding,
  hasPaymentMethod,
  isClientOnALaCartePlan,
  isClientPayingSubscriber,
  isFrictionlessSignupUser,
} from '@frontend/api-client';
import type { Client, ClientStatus } from '@frontend/api-types';
import { P, match } from 'ts-pattern';

export type RedirectDecision =
  | {
      action: 'allow';
      reason: 'allow';
    }
  | {
      action: 'defer';
      reason: 'client-not-ready' | 'growthbook-not-ready';
    }
  | {
      action: 'redirect';
      reason:
        | 'app-index-home'
        | 'membership-incomplete'
        | 'onboarding-complete'
        | 'onboarding-step-backward'
        | 'onboarding-step-forward'
        | 'profile-incomplete'
        | 'signup-changes-disabled'
        | 'unauthenticated';
      search?: {
        description?: string;
        promo?: string;
        redirect?: string;
      };
      to: string;
    };

export type RedirectPolicyContext = {
  description?: string;
  href: string;
  isAuthenticated: boolean;
  isClientLoading: boolean;
  isClientStatusLoading: boolean;
  isGrowthbookReady: boolean;
  isSignupChangesEnabled: boolean;
  membershipComplete: boolean;
  onboardingComplete: boolean;
  pathname: string;
  profileComplete: boolean;
  promo?: string;
};

export type OnboardingRedirectContext = Pick<
  RedirectPolicyContext,
  'description' | 'membershipComplete' | 'pathname' | 'profileComplete' | 'promo'
>;

const ONBOARDING_PATHS = ['/onboarding/complete', '/onboarding/membership', '/onboarding/profile'] as const;

export const isProfileComplete = (client: MaybeUndefined<Client>): boolean =>
  isFrictionlessSignupUser(client) || Boolean(client?.birthday && client?.phoneNumber);

const isHouseholdMember = (client: MaybeUndefined<Client>): boolean =>
  Boolean(client?.household && client?.isHouseholdOwner === false);

export const isMembershipComplete = ({
  client,
  clientStatus,
}: {
  client: MaybeUndefined<Client>;
  clientStatus: MaybeUndefined<ClientStatus>;
}): boolean =>
  isFrictionlessSignupUser(client) ||
  isClientPayingSubscriber({ client, clientStatus }) ||
  (isClientOnALaCartePlan(client) && hasPaymentMethod(clientStatus)) ||
  hasCompletedOnboarding(clientStatus) ||
  isHouseholdMember(client);

export const getPostLoginRedirect = ({
  client,
  clientStatus,
  isSignupChangesEnabled,
  redirect,
}: {
  client: MaybeUndefined<Client>;
  clientStatus: MaybeUndefined<ClientStatus>;
  isSignupChangesEnabled: boolean;
  redirect?: string;
}): string | '/app/home' | '/onboarding/membership' | '/onboarding/profile' => {
  if (!isSignupChangesEnabled) {
    return '/app/home';
  }

  if (redirect && redirect.startsWith('/') && !redirect.startsWith('//') && !redirect.includes('://')) {
    return redirect;
  }

  if (isFrictionlessSignupUser(client)) {
    return '/app/home';
  }

  if (!isMembershipComplete({ client, clientStatus })) {
    return '/onboarding/membership';
  }

  if (!isProfileComplete(client)) {
    return '/onboarding/profile';
  }

  return '/app/home';
};

const normalizePathname = (pathname: string) => {
  if (pathname === '/') {
    return pathname;
  }

  return pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
};

const isOnboardingPath = (pathname: string) => ONBOARDING_PATHS.includes(pathname as (typeof ONBOARDING_PATHS)[number]);

const isProtectedPath = (pathname: string) => pathname.startsWith('/app') || isOnboardingPath(pathname);

const getOnboardingSearchParams = ({
  description,
  promo,
}: {
  description?: string;
  promo?: string;
}): MaybeUndefined<{
  description?: string;
  promo?: string;
}> => {
  const onboardingSearch = {
    description,
    promo,
  };

  return Object.values(onboardingSearch).some(Boolean) ? onboardingSearch : undefined;
};

const ALLOW_REDIRECT_DECISION: RedirectDecision = {
  action: 'allow',
  reason: 'allow',
};

const getOnboardingHomeRedirect = (): RedirectDecision => ({
  action: 'redirect',
  reason: 'onboarding-complete',
  to: '/app/home',
});

const getOnboardingProfileRedirect = ({
  description,
  promo,
}: {
  description?: string;
  promo?: string;
}): RedirectDecision => ({
  action: 'redirect',
  reason: 'onboarding-step-forward',
  search: getOnboardingSearchParams({
    description,
    promo,
  }),
  to: '/onboarding/profile',
});

const getOnboardingMembershipRedirect = ({
  description,
  promo,
}: {
  description?: string;
  promo?: string;
}): RedirectDecision => ({
  action: 'redirect',
  reason: 'onboarding-step-backward',
  search: getOnboardingSearchParams({
    description,
    promo,
  }),
  to: '/onboarding/membership',
});

export const getOnboardingRedirect = (context: OnboardingRedirectContext): RedirectDecision => {
  const pathname = normalizePathname(context.pathname);

  if (!isOnboardingPath(pathname)) {
    return ALLOW_REDIRECT_DECISION;
  }

  const onboardingPathname = pathname as (typeof ONBOARDING_PATHS)[number];

  return match<[path: (typeof ONBOARDING_PATHS)[number], membershipComplete: boolean, profileComplete: boolean]>([
    onboardingPathname,
    context.membershipComplete,
    context.profileComplete,
  ])
    .with(['/onboarding/membership', false, P._], () => ALLOW_REDIRECT_DECISION)
    .with(['/onboarding/membership', true, false], () =>
      getOnboardingProfileRedirect({
        description: context.description,
        promo: context.promo,
      }),
    )
    .with(['/onboarding/membership', true, true], getOnboardingHomeRedirect)
    .with(['/onboarding/complete', P._, P._], () => ALLOW_REDIRECT_DECISION)
    .with(['/onboarding/profile', false, P._], () =>
      getOnboardingMembershipRedirect({
        description: context.description,
        promo: context.promo,
      }),
    )
    .with(['/onboarding/profile', true, false], () => ALLOW_REDIRECT_DECISION)
    .with(['/onboarding/profile', true, true], getOnboardingHomeRedirect)
    .exhaustive();
};

/**
 * Redirect decision matrix for authenticated onboarding/app routes.
 *
 * | isAuthenticated | isGrowthbookReady | isSignupChangesEnabled | client loading | membershipComplete | profileComplete | path | expected |
 * | --- | --- | --- | --- | --- | --- | --- | --- |
 * | false | any | any | any | any | any | protected route | /login?redirect=... |
 * | true | false | any | any | any | any | onboarding/app routes | defer |
 * | true | true | false | false | any | any | onboarding routes | /app/home |
 * | true | true | true | true | any | any | onboarding/app routes | defer |
 * | true | true | true | false | any | any | /app routes with onboardingComplete=true | do not enforce onboarding redirects |
 * | true | true | true | false | false | any | /app routes | /onboarding/membership |
 * | true | true | true | false | true | false | /app routes | /onboarding/profile |
 * | true | true | true | false | true | true | /app/ | /app/home |
 */
export const getRedirectDecision = (context: RedirectPolicyContext): RedirectDecision => {
  const pathname = normalizePathname(context.pathname);
  const protectedPath = isProtectedPath(pathname);

  if (!context.isAuthenticated && protectedPath) {
    return {
      action: 'redirect',
      reason: 'unauthenticated',
      search: { redirect: context.href },
      to: '/login',
    };
  }

  if (!protectedPath) {
    return {
      action: 'allow',
      reason: 'allow',
    };
  }

  if (!context.isGrowthbookReady) {
    return {
      action: 'defer',
      reason: 'growthbook-not-ready',
    };
  }

  if (isOnboardingPath(pathname) && !context.isSignupChangesEnabled) {
    return {
      action: 'redirect',
      reason: 'signup-changes-disabled',
      to: '/app/home',
    };
  }

  if (context.isClientLoading || context.isClientStatusLoading) {
    return {
      action: 'defer',
      reason: 'client-not-ready',
    };
  }

  const shouldEnforceOnboardingRedirects = context.isSignupChangesEnabled && !context.onboardingComplete;

  if (pathname.startsWith('/app') && shouldEnforceOnboardingRedirects && !context.membershipComplete) {
    return {
      action: 'redirect',
      reason: 'membership-incomplete',
      search: getOnboardingSearchParams({
        description: context.description,
        promo: context.promo,
      }),
      to: '/onboarding/membership',
    };
  }

  if (pathname.startsWith('/app') && shouldEnforceOnboardingRedirects && !context.profileComplete) {
    return {
      action: 'redirect',
      reason: 'profile-incomplete',
      search: getOnboardingSearchParams({
        description: context.description,
        promo: context.promo,
      }),
      to: '/onboarding/profile',
    };
  }

  if (pathname === '/app') {
    return {
      action: 'redirect',
      reason: 'app-index-home',
      to: '/app/home',
    };
  }

  return ALLOW_REDIRECT_DECISION;
};
